import { useMemo, useState } from "react";
import clsx from "clsx";
import type { Article } from "@/types/camping";
import { formatCurrency, grossAmountFromNet } from "@/lib/money";
import { pageDisplayLabel } from "@/lib/posTiles";

export interface QuickSaleTilePage {
  label: string;
  tiles: Article[];
}

export interface QuickSaleProductTilesProps {
  pages: QuickSaleTilePage[];
  disabled?: boolean;
  disabledHint?: string;
  onSelect: (article: Article) => void;
}

const MONOGRAM_TONES = [
  "bg-accent-soft text-accent",
  "bg-[color-mix(in_srgb,var(--eh-indigo)_16%,transparent)] text-status-indigo",
  "bg-[color-mix(in_srgb,var(--eh-green)_18%,transparent)] text-status-green",
  "bg-[color-mix(in_srgb,var(--eh-orange)_20%,transparent)] text-status-orange",
];

function tilePriceLabel(article: Article): string | null {
  if (article.current_price == null) return null;
  const gross = grossAmountFromNet(article.current_price, article.tax_rate_percent);
  return gross != null ? formatCurrency(String(gross.toFixed(2))) : null;
}

function monogramTone(id: string): string {
  let hash = 0;
  for (let i = 0; i < id.length; i += 1) {
    hash = (hash + id.charCodeAt(i)) % MONOGRAM_TONES.length;
  }
  return MONOGRAM_TONES[hash] ?? MONOGRAM_TONES[0]!;
}

function articleInitial(name: string): string {
  const letter = name.trim().charAt(0).toUpperCase();
  return letter || "·";
}

export function QuickSaleProductTiles({
  pages,
  disabled = false,
  disabledHint,
  onSelect,
}: QuickSaleProductTilesProps) {
  const visiblePages = useMemo(
    () => pages.map((page, index) => ({ ...page, index })).filter((page) => page.tiles.length > 0),
    [pages],
  );

  const [activePageIndex, setActivePageIndex] = useState(0);

  const safeActiveIndex = Math.min(activePageIndex, Math.max(visiblePages.length - 1, 0));
  const activePage = visiblePages[safeActiveIndex];

  if (!activePage) return null;

  const showTabs = visiblePages.length > 1;

  return (
    <section className="rounded-2xl border border-separator bg-card p-3 shadow-ios sm:p-4">
      <div className="flex items-baseline justify-between gap-3">
        <h2 className="text-sm font-semibold text-label">Artikel</h2>
        <p className="text-xs text-label-tertiary">Preise inkl. MwSt.</p>
      </div>

      {disabled && disabledHint ? (
        <p className="mt-2 rounded-xl bg-fill-quaternary px-3 py-2 text-xs leading-relaxed text-label-secondary">
          {disabledHint}
        </p>
      ) : null}

      {showTabs ? (
        <div
          className="mt-3 flex gap-1.5 overflow-x-auto pb-0.5"
          role="tablist"
          aria-label="Kachel-Seiten"
        >
          {visiblePages.map((page, idx) => {
            const selected = idx === safeActiveIndex;
            return (
              <button
                key={page.index}
                type="button"
                role="tab"
                aria-selected={selected}
                className={clsx(
                  "shrink-0 rounded-full px-3 py-1.5 text-sm font-medium transition-colors",
                  selected
                    ? "bg-accent text-label-on-accent shadow-ios"
                    : "bg-fill-tertiary text-label-secondary hover:bg-fill hover:text-label",
                )}
                onClick={() => setActivePageIndex(idx)}
              >
                {pageDisplayLabel(page.label, page.index)}
              </button>
            );
          })}
        </div>
      ) : null}

      <div
        className="mt-3 grid grid-cols-2 gap-2.5 sm:grid-cols-3 lg:grid-cols-2 xl:grid-cols-3"
        role="group"
        aria-label="Schnellauswahl Artikel"
      >
        {activePage.tiles.map((article) => {
          const price = tilePriceLabel(article);
          return (
            <button
              key={article.id}
              type="button"
              disabled={disabled}
              className="group flex min-h-[7.25rem] flex-col overflow-hidden rounded-2xl border border-separator bg-card text-left shadow-ios transition duration-150 hover:-translate-y-0.5 hover:border-accent/40 hover:shadow-ios-md active:translate-y-0 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-60 disabled:shadow-none disabled:hover:translate-y-0 disabled:hover:border-separator"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => onSelect(article)}
            >
              <span
                className={clsx(
                  "flex h-12 items-center justify-center overflow-hidden",
                  article.shop_image_url ? "bg-fill-tertiary" : monogramTone(article.id),
                )}
              >
                {article.shop_image_url ? (
                  <img
                    src={article.shop_image_url}
                    alt=""
                    className="h-full w-full object-cover"
                    loading="lazy"
                  />
                ) : (
                  <span className="text-lg font-semibold" aria-hidden>
                    {articleInitial(article.name)}
                  </span>
                )}
              </span>
              <span className="flex flex-1 flex-col justify-between px-2.5 py-2">
                <span className="line-clamp-2 text-sm font-semibold leading-snug text-label">
                  {article.name}
                </span>
                <span className="mt-1.5 flex items-end justify-between gap-1">
                  <span className="text-sm font-semibold tabular-nums text-accent">
                    {price ?? "Preis offen"}
                  </span>
                  {article.requires_pickup_receipt ? (
                    <span className="text-[10px] font-medium uppercase tracking-wide text-status-orange">
                      Abholung
                    </span>
                  ) : null}
                </span>
              </span>
            </button>
          );
        })}
      </div>
    </section>
  );
}
